import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { KeyRound, RefreshCw, Trash2, Link2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMBED_KEY = '25for25_embed_url';

const formatWhen = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const AccessTab = () => {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [grantEmail, setGrantEmail] = useState('');
  const [granting, setGranting] = useState(false);
  const [message, setMessage] = useState('');
  const [embedUrl, setEmbedUrl] = useState('');
  const [savingUrl, setSavingUrl] = useState(false);
  const [urlMessage, setUrlMessage] = useState('');

  const fetchAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [grantsRes, settingRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/access-grants`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/settings/${EMBED_KEY}`, { withCredentials: true }),
      ]);
      setGrants(grantsRes.data);
      setEmbedUrl(settingRes.data.value || '');
    } catch (err) {
      setError(err.response?.status === 401 || err.response?.status === 403
        ? 'Not authorized — please log in as admin.'
        : 'Could not load access list. Try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGrant = async (e) => {
    e.preventDefault();
    if (!grantEmail.trim()) return;
    setGranting(true);
    setMessage('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/admin/access-grants`,
        { email: grantEmail.trim() }, { withCredentials: true });
      setMessage(data.already_had_access
        ? `${data.email} already had access — nothing changed.`
        : `✅ Access granted to ${data.email}`);
      setGrantEmail('');
      fetchAll();
    } catch (err) {
      setMessage(err.response?.data?.detail?.[0]?.msg || err.response?.data?.detail || 'Could not grant access — check the email format.');
    } finally {
      setGranting(false);
    }
  };

  const handleRevoke = async (email) => {
    if (!window.confirm(`Revoke 25 for 25 access for ${email}?`)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/access-grants/${encodeURIComponent(email)}`, { withCredentials: true });
      setMessage(`Access revoked for ${email}`);
      fetchAll();
    } catch {
      setMessage('Could not revoke access.');
    }
  };

  const handleSaveUrl = async (e) => {
    e.preventDefault();
    setSavingUrl(true);
    setUrlMessage('');
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings/${EMBED_KEY}`,
        { value: embedUrl.trim() }, { withCredentials: true });
      setUrlMessage(embedUrl.trim()
        ? '✅ Saved — purchasers now see the app at /workouts/25-for-25'
        : 'Saved — embed URL cleared, purchasers see the "coming soon" screen.');
    } catch {
      setUrlMessage('Could not save the URL.');
    } finally {
      setSavingUrl(false);
    }
  };

  return (
    <div className="chat-tab" data-testid="admin-access-tab">
      <div className="chat-tab-header">
        <div>
          <h2><KeyRound size={22} /> 25 for 25 Access</h2>
          <p className="chat-tab-sub">Members with lifetime access — Stripe purchases unlock automatically; grant manually for cash/in-person sales.</p>
        </div>
        <button className="chat-refresh-btn" onClick={fetchAll} data-testid="access-refresh-btn">
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      <form onSubmit={handleSaveUrl} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '10px' }}>
        <Link2 size={16} style={{ flexShrink: 0, color: '#6b7280' }} />
        <input
          type="url"
          placeholder="Workout app URL (leave empty until the app is hosted)"
          value={embedUrl}
          onChange={(e) => setEmbedUrl(e.target.value)}
          style={{ flex: 1, minWidth: '260px', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
          data-testid="access-embed-url-input"
        />
        <button type="submit" className="chat-refresh-btn" disabled={savingUrl} data-testid="access-embed-url-save">
          {savingUrl ? 'Saving…' : 'Save URL'}
        </button>
      </form>
      {urlMessage && <p className="chat-tab-status" data-testid="access-url-message">{urlMessage}</p>}

      <form onSubmit={handleGrant} style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', margin: '14px 0' }}>
        <input
          type="email"
          placeholder="member@email.com"
          value={grantEmail}
          onChange={(e) => setGrantEmail(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px' }}
          data-testid="access-grant-email-input"
        />
        <button type="submit" className="chat-refresh-btn" disabled={granting || !grantEmail.trim()} data-testid="access-grant-btn">
          {granting ? 'Granting…' : 'Grant Access'}
        </button>
      </form>
      {message && <p className="chat-tab-status" data-testid="access-grant-message">{message}</p>}

      {loading && <p className="chat-tab-status">Loading…</p>}
      {error && <p className="chat-tab-status chat-tab-error">{error}</p>}
      {!loading && !error && grants.length === 0 && (
        <p className="chat-tab-status">No one has access yet. Stripe purchases will appear here automatically.</p>
      )}

      {!loading && !error && grants.length > 0 && (
        <div className="spam-log-table" data-testid="access-grants-table">
          <div className="spam-log-row spam-log-head">
            <span>Member</span>
            <span>Source</span>
            <span>Granted</span>
            <span></span>
          </div>
          {grants.map((g) => (
            <div className="spam-log-row" key={g.email}>
              <span className="spam-email">{g.email}</span>
              <span style={{ color: g.source === 'stripe' ? '#16a34a' : '#d97706', fontWeight: 600 }}>
                {g.source === 'stripe' ? 'Stripe purchase' : 'Manual grant'}
              </span>
              <span>{formatWhen(g.granted_at)}</span>
              <span>
                <button
                  onClick={() => handleRevoke(g.email)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626' }}
                  title="Revoke access"
                  data-testid={`access-revoke-${g.email}`}
                >
                  <Trash2 size={16} />
                </button>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccessTab;
